import React from 'react';
import PropTypes from 'prop-types';

import decorate from '_core/utils/decorate';
import * as http from '_core/utils/http';

const VIDEO_DEFAULT_RATIO = 16 / 9;

const VIDEO_TYPE_MAP = {
  facebook: {
    url: (show) =>
      `https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2F${encodeURIComponent(
        show.data.user
      )}%2Fvideos%2F${encodeURIComponent(show.data.id)}%2F`,
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"stopVideo","args":""}',
        '*'
      ),
    sources: [
      {
        host: /^(facebook|fb)\.com$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(?<user>[^\/]+)\/videos\/(?<id>[^\/]+)/.exec(
            (info.uri === 'plugins/video.php' &&
              http.parse(info.query.href)?.uri) ||
              info.uri
          )?.groups
        })
      }
    ]
  },

  kinescope: {
    url: (show) => `https://kinescope.io/embed/${show.data.id}`,
    autoplay: (show) => (show.src = http.param(show.src, 'autoplay=1')),
    loop: (show) => (show.src = http.param(show.src, 'loop=1')),
    mute: (show) => (show.src = http.param(show.src, 'muted=1')),
    uncontrollable: (show) =>
      (show.src = http.param(show.src, 'controls=0&keyboard=0')),
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"stopVideo","args":""}',
        '*'
      ),
    sources: [
      {
        host: /^kinescope\.io$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(embed\/)?(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      }
    ]
  },

  odnoklassniki: {
    url: (show) => `https://ok.ru/videoembed/${show.data.id}`,
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"stopVideo","args":""}',
        '*'
      ),
    sources: [
      {
        host: /^(odnoklassniki|ok)\.ru$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(videoembed|video|live)\/(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      }
    ]
  },

  rutube: {
    url: (show) => `https://rutube.ru/embed/${show.data.id}`,
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"type":"player:play","data":{}}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"type":"player:pause","data":{}}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"type":"player:stop","data":{}}',
        '*'
      ),
    sources: [
      {
        host: /^rutube\.ru$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(embed|video(\/private)?)\/(?<id>[^\/]+)/.exec(info.uri)
            ?.groups
        })
      }
    ]
  },

  tiktok: {
    url: (show) => `https://www.tiktok.com/embed/v2/${show.data.id}`,
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"stopVideo","args":""}',
        '*'
      ),
    sources: [
      {
        host: /^tiktok\.com$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /@[^\/]+\/video\/(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      }
    ]
  },

  vimeo: {
    url: (show) => `https://player.vimeo.com/video/${show.data.id}`,
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"stopVideo","args":""}',
        '*'
      ),
    sources: [
      {
        host: /^vimeo\.com$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      },
      {
        host: /^player\.vimeo\.com$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /video\/(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      }
    ]
  },

  youtube: {
    url: (show) =>
      `https://www.youtube.com/embed/${show.data.id}?enablejsapi=1&rel=0&showinfo=0&modestbranding=1`,
    autoplay: (show) => (show.src = http.param(show.src, 'autoplay=1')),
    loop: (show) =>
      (show.src = http.param(
        show.src,
        'loop=1&playlist=' + encodeURIComponent(show.id)
      )),
    mute: (show) => (show.src = http.param(show.src, 'mute=1')),
    preview: (show) =>
      (show.preview =
        'https://img.youtube.com/vi/' +
        encodeURIComponent(show.id) +
        '/hqdefault.jpg'),
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        '*'
      ),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"pauseVideo","args":""}',
        '*'
      ),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage(
        '{"event":"command","func":"stopVideo","args":""}',
        '*'
      ),
    sources: [
      {
        host: /^youtu\.be$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      },
      {
        host: /^youtube\.\w+$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data:
            info.uri === 'watch'
              ? { id: info.query.v }
              : /embed\/(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      }
    ]
  },

  vkvideo: {
    url: (show) =>
      `https://vkvideo.ru/video_ext.php?oid=${show.data.oid}&id=${show.data.id}&hd=2`,
    autoplay: (show) => (show.src = http.param(show.src, 'autoplay=1')),
    loop: (show) => (show.src = http.param(show.src, 'loop=1')),
    mute: (show) => (show.src = http.param(show.src, 'muted=1')),
    play: (video) =>
      video.iframeRef.current?.contentWindow.postMessage('play', '*'),
    pause: (video) =>
      video.iframeRef.current?.contentWindow.postMessage('pause', '*'),
    stop: (video) =>
      video.iframeRef.current?.contentWindow.postMessage('stop', '*'),
    sources: [
      {
        host: /^vkvideo\.ru$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: /(video)?(?<oid>[^\/_]+)_(?<id>[^\/]+)/.exec(info.uri)?.groups
        })
      }
    ]
  },

  file: {
    url: (show) => `${show.data.id}`,
    autoplay: (show) => (show.props.autoPlay = true),
    loop: (show) => (show.props.loop = true),
    mute: (show) => (show.props.muted = true),
    uncontrollable: (show) => (show.props.controls = false),
    sources: [
      {
        src: /\.(m4v|ogv|webm|mp4)$/,
        info: (info, src) => ({
          ratio: VIDEO_DEFAULT_RATIO,
          data: { id: src },
          props: { controls: true }
        })
      }
    ]
  }
};

const VIDEO_SOURCE_LIST = Object.entries(VIDEO_TYPE_MAP).flatMap(
  ([code, type]) => (
    type.sources.forEach((source) => (source.type = code)), type.sources
  )
);

const Video = decorate(
  class extends React.PureComponent {
    static decorateWith = {};

    static propTypes = {
      type: PropTypes.string.isRequired,
      src: PropTypes.string,
      data: PropTypes.object,
      ratio: PropTypes.number,
      allow: PropTypes.arrayOf(PropTypes.string).isRequired,
      autoplay: PropTypes.bool.isRequired
    };

    static defaultProps = {
      type: 'auto',
      allow: [
        'fullscreen',
        'accelerometer',
        'autoplay',
        'clipboard-write',
        'encrypted-media',
        'gyroscope',
        'picture-in-picture'
      ],
      autoplay: false
    };

    constructor(props) {
      super(props);

      this.iframeRef = React.createRef();
    }

    getShowInfo = ({
      type,
      src,
      data,
      ratio,
      preview,
      autoplay,
      loop,
      mute,
      uncontrollable
    }) => {
      const show = {
        type: type || 'auto',
        src,
        data,
        ratio: Math.max(ratio, 0) || VIDEO_DEFAULT_RATIO
      };

      if (!src) {
        show.type = 'file';

        return show;
      }

      if (show.src.startsWith('//')) show.src = 'https:' + show.src;

      const info = http.parse(show.src);

      if (show.type === 'auto') {
        if (!info) {
          show.type = 'file';

          return show;
        }

        if (info.host?.startsWith('www.'))
          info.host = info.host.substring('www.'.length);

        const infoPrepare = VIDEO_SOURCE_LIST.find((source) => {
          let result = false;

          if (!result && source.host) result = source.host.test(info.host);

          if (!result && source.uri) result = source.uri.test(info.uri);

          if (!result && source.src) result = source.src.test(src);

          return result;
        });

        if (infoPrepare) {
          const infoData = infoPrepare.info(info, show.src);

          show.type = infoPrepare.type;
          show.data = { ...show.data, ...infoData.data };
          show.props = infoData.props;

          if (!show.ratio) show.ratio = infoData.ratio;
        }
      }

      const typeInfo = VIDEO_TYPE_MAP[show.type];

      if (typeInfo) {
        show.info = typeInfo;
        show.src = typeInfo.url(show);

        if (preview && typeInfo.preview) typeInfo.preview(show);

        if (!preview && autoplay && typeInfo.autoplay) typeInfo.autoplay(show);

        if (!preview && loop && typeInfo.loop) typeInfo.loop(show);

        if (!preview && mute && typeInfo.mute) typeInfo.mute(show);

        if ((preview || uncontrollable) && typeInfo.uncontrollable)
          typeInfo.uncontrollable(show);
      }

      return show;
    };

    mountVideo = ({ id, preview }) => {
      if (preview) return;

      let set = videoMap.get(id || '');

      if (!set) videoMap.set(id || '', (set = new Set()));

      set.add(this);
    };

    unmountVideo = ({ id, preview }) => {
      if (preview) return;

      let set = videoMap.get(id || '');

      if (set) set.delete(this);
    };

    componentDidMount() {
      this.mountVideo(this.props);
    }

    componentWillUnmount() {
      this.unmountVideo(this.props);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      this.unmountVideo(prevProps);
      this.mountVideo(this.props);
    }

    render() {
      const show = (this.show = this.getShowInfo(this.props));

      const {
        id,
        className,
        containerClassName,
        dataClassName,
        overlayClassName,
        style,
        alt,
        allow,
        preview,
        uncontrollable,
        before,
        after,
        children,

        ...props
      } = this.props;

      delete props.type;
      delete props.src;
      delete props.data;
      delete props.ratio;
      delete props.autoplay;
      delete props.loop;
      delete props.mute;

      return (
        <div
          {...props}
          id={id}
          className={className}
          style={{ display: 'inline-block', width: '100%', ...style }}
        >
          <div
            className={containerClassName}
            style={{
              position: 'relative',
              width: '100%',
              height: 0,
              paddingBottom: 100 / show.ratio + '%'
            }}
          >
            {before}

            {show.preview ? (
              <img
                src={show.preview}
                alt={alt || ''}
                className={dataClassName}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : show.src ? (
              <>
                {show.type === 'file' ? (
                  <video
                    ref={this.iframeRef}
                    src={show.src}
                    className={dataClassName}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%'
                    }}
                    {...show.props}
                  />
                ) : (
                  <iframe
                    ref={this.iframeRef}
                    type="text/html"
                    src={show.src}
                    frameBorder="0"
                    allowFullScreen={allow.indexOf('fullscreen') >= 0}
                    allow={allow.join('; ')}
                    className={dataClassName}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%'
                    }}
                    {...show.props}
                  />
                )}

                {preview || uncontrollable ? (
                  <div
                    className={overlayClassName}
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      width: '100%',
                      height: '100%'
                    }}
                  ></div>
                ) : null}
              </>
            ) : (
              children || null
            )}

            {after}
          </div>
        </div>
      );
    }
  }
);

Video.get = (id) => {
  if (id === undefined) {
    const result = [];

    videoMap.forEach((value, key) => result.push(...Video.get(id || '')));

    return result;
  } else {
    const set = videoMap.get(id || '');

    return set ? [...set] : [];
  }
};

Video.play = (id) =>
  Video.get(id).forEach((video) => video.show?.info?.play(video));
Video.pause = (id) =>
  Video.get(id).forEach((video) => video.show?.info?.pause(video));
Video.stop = (id) =>
  Video.get(id).forEach((video) => video.show?.info?.stop(video));

export default Video;

const videoMap = new Map();
